import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MonitorStream } from '@apis/monitorStream';
import type { MonitorStreamEvent } from '@interfaces/IMonitorApi';
import { useAppDispatch } from '@store/hooks';
import { applyStreamEvents, setStreamStatus } from '@store/slices/monitor.slice';

/** Trusstor sends a wave of updates, one per badge that moved, spread over a
 *  second or two; a badge's old and new floor arrive apart, so applying each
 *  update alone flashes the chip off both floors (or on both) mid-wave. A wave
 *  lands once it goes quiet for this long… */
export const STREAM_QUIET_MS = 300;
/** …or after this long at most, so the page never lags far behind. */
export const STREAM_MAX_WAIT_MS = 2000;

export function useMonitorStream(siteId: string | null) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!siteId) return;
    let queued: MonitorStreamEvent[] = [];
    let waveStartedAt = 0;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      dispatch(applyStreamEvents(queued));
      queued = [];
    };
    const stream = new MonitorStream(siteId, {
      onEvent: (event) => {
        if (queued.length === 0) waveStartedAt = Date.now();
        queued.push(event);
        clearTimeout(flushTimer);
        // A safety alert never waits behind a wave (and lands after it, in order).
        if (event.channel === 'safety') flush();
        else flushTimer = setTimeout(flush, Math.min(STREAM_QUIET_MS, waveStartedAt + STREAM_MAX_WAIT_MS - Date.now()));
      },
      onResync: (_channel) => {
        // v1: refetch the whole snapshot. v2 could use a per-channel snapshot endpoint.
        void queryClient.invalidateQueries({ queryKey: ['monitor', 'snapshot', siteId] });
      },
      onTokenExpiring: () => {
        // The next authed fetch (snapshot refetch, etc.) will go through
        // authedFetch, which silently refreshes the JWT and retries; the SSE
        // stream itself can't carry the new token mid-connection because
        // EventSource bakes the URL at construction time. A future improvement
        // could close + reopen the stream with the refreshed access token.
      },
      onOpen: () => dispatch(setStreamStatus('open')),
      onClose: () => dispatch(setStreamStatus('closed')),
      onError: () => dispatch(setStreamStatus('reconnecting')),
    });
    dispatch(setStreamStatus('connecting'));
    void stream.open();
    return () => {
      // A closing stream drops a half-gathered wave: the next site's snapshot replaces it.
      clearTimeout(flushTimer);
      stream.close();
    };
  }, [siteId, dispatch, queryClient]);
}
